import { AxiosError, type AxiosRequestConfig } from "axios";
import { get, patch, post } from "./api";
import { API } from "@/constants";
import { normalizeWarehouseBlock } from "@/utils/onboardingWarehouseCandidates";
import type {
  AdminOnboardingResponse,
  AdminReadinessPatch,
  OnboardingContinueRequest,
  OnboardingContinueResponse,
  OnboardingSourcesResponse,
  EnsureOnboardingTrialsResponse,
  OnboardingStatusResponse,
  OnboardingWarehouseResponse,
  WizardCompleteResponse,
} from "@/types/onboarding.types";

/** Suppress global error toasts — callers (e.g. setup API console) show errors inline. */
const silent: AxiosRequestConfig = { skipGlobalErrorHandler: true };

/** Fetch the current company's onboarding gate and wizard status. */
export const fetchOnboardingStatus = () =>
  get<OnboardingStatusResponse>(API.ONBOARDING.STATUS, silent);

/** IQ + Daton trials for company admins in `/setup` (Daton Step 1 subscribe gate). */
export const postOnboardingEnsureTrials = () =>
  post<EnsureOnboardingTrialsResponse>(API.ONBOARDING.ENSURE_TRIALS, undefined, silent);

/** Fetch the current company's BigQuery warehouse connection status from the BFF. */
export const fetchOnboardingWarehouse = async (): Promise<OnboardingWarehouseResponse> => {
  const response = await get<OnboardingWarehouseResponse>(API.ONBOARDING.WAREHOUSE, silent);
  return { ...response, warehouse: normalizeWarehouseBlock(response.warehouse) };
};

/**
 * Delete one of the company's Daton warehouses (same call Daton's own UI makes).
 * No `silent` config: the axios client toasts a failure once, so callers must not.
 */
export const postOnboardingWarehouseDelete = (warehouseId: number) =>
  post<{ deleted: boolean }>(API.ONBOARDING.WAREHOUSE_DELETE(warehouseId));

export interface FetchOnboardingSourcesOptions {
  /** Bypass BFF catalog cache after returning from Pulse/Daton (D23). */
  refresh?: boolean;
}

/**
 * Fetch the unified Pulse + Daton source catalog and connected status.
 *
 * @param options - Optional flags such as `refresh: true` to bypass BFF cache.
 */
export const fetchOnboardingSources = (options?: FetchOnboardingSourcesOptions) => {
  const params =
    options?.refresh ? { refresh: "1" } : undefined;
  return get<OnboardingSourcesResponse>(API.ONBOARDING.SOURCES, {
    ...silent,
    params,
  });
};

/**
 * Validate Step 1 + Step 2 readiness on the BFF before completing the setup wizard.
 * Falls back to the legacy `/onboarding/step2/continue` endpoint on 404.
 *
 * @param body - Continue request payload.
 */
export const postOnboardingSourcesContinue = async (
  body: OnboardingContinueRequest,
): Promise<OnboardingContinueResponse> => {
  const withTypedWarehouse = (response: OnboardingContinueResponse): OnboardingContinueResponse => ({
    ...response,
    warehouse: normalizeWarehouseBlock(response.warehouse),
  });
  try {
    return withTypedWarehouse(
      await post<OnboardingContinueResponse>(API.ONBOARDING.SOURCES_CONTINUE, body, silent),
    );
  } catch (err) {
    if (
      err instanceof AxiosError &&
      err.response?.status === 404 &&
      API.ONBOARDING.SOURCES_CONTINUE_LEGACY
    ) {
      return withTypedWarehouse(
        await post<OnboardingContinueResponse>(
          API.ONBOARDING.SOURCES_CONTINUE_LEGACY,
          body,
          silent,
        ),
      );
    }
    throw err;
  }
};

/** Mark the company's onboarding setup wizard as completed. */
export const postOnboardingWizardComplete = () =>
  post<WizardCompleteResponse>(API.ONBOARDING.WIZARD_COMPLETE, undefined, silent);

/**
 * Fetch onboarding state for a specific company (Admin view).
 *
 * @param companyId - Target company ID.
 */
export const fetchAdminCompanyOnboarding = (companyId: number) =>
  get<AdminOnboardingResponse>(API.ONBOARDING.ADMIN_COMPANY(companyId), silent);

/**
 * Update a company's onboarding readiness or required sources (Admin view).
 *
 * @param companyId - Target company ID.
 * @param body - Fields to patch on the company onboarding record.
 */
export const patchAdminCompanyReadiness = (
  companyId: number,
  body: AdminReadinessPatch,
) =>
  patch<AdminOnboardingResponse>(
    API.ONBOARDING.ADMIN_READINESS(companyId),
    body,
    silent,
  );
